// Pure core of group cancel & refund (Phase 17, CXL-01/02/03). Zero UI.
//
// Option B (17-RESEARCH §5, RESOLVED): the refund figure is FRONTEND-DERIVED from
// backend-authoritative inputs — Σ(row.total_cents) when the backend-served
// event.start_time is more than 8h out, else $0 (D-02/D-05). There is no events
// cancel-preview endpoint; deleteJoin (events/{id}/join/) already cancels self +
// all child rows because child rows carry member = parent.
//
// Grouping (#8 — per EVENT, not per checkout): the events cancel endpoint
// (DELETE events/{id}/join/) cancels EVERY non-cancelled booking the member has
// for that event, across ALL checkouts (EventEntryView.delete filters only by
// member+event). So the cancelable unit and the disclosure MUST be the whole
// event: all the member's rows for one event.id collapse into ONE group, and the
// refund figure sums every one of those rows. This makes the single deleteJoin
// action match exactly what we disclose (no over-cancel / under-disclose). Rows
// with no event.id stay standalone (legacy fallback).
//
// Time discipline: the 8h threshold is a single millisecond arithmetic comparison
// against the backend-served start_time. NEVER toISOString() (project memory: UTC
// shift bug). Affected names project through buildBookedList — display_name only,
// never DOB (WHO-01/D-06 privacy chokepoint).

import { buildBookedList } from '../../functions/buildBookedList'

// One refundable group = all of the member's bookings for a single event.
export interface GroupedBooking {
  eventId: string
  // Event card title, surfaced in the modal's "Cancel your booking(s) for X"
  // copy. Optional so test fixtures / legacy rows without a title still type.
  eventTitle?: string
  startTime: string
  rows: any[]
  // Legacy/unused: the first row's checkout. Grouping no longer keys on it
  // (kept optional so pre-existing fixtures compile). Prefer eventId.
  checkout?: string | null
}

// Backend-authoritative-derived disclosure surfaced before confirm.
export interface GroupCancelDisclosure {
  refundable: boolean
  // Cash back, in cents — sum of the money-paid (paid_directly) rows only.
  refundCents: number
  // Class-pack credits returned — count of the pack-paid (paid_directly === false)
  // rows. A pack booking refunds a credit, NOT its total_cents in cash.
  creditCount: number
}

const EIGHT_HOURS_MS = 8 * 60 * 60 * 1000
// Honesty buffer (RESOLVED mitigation a): borderline clock skew near the 8h
// boundary resolves to the lower/$0 disclosure so we never over-promise a refund.
const HONESTY_BUFFER_MS = 5 * 60 * 1000

// Group input rows by their event id. All of the member's rows for one event.id
// collapse into a single group — regardless of how many checkouts they span —
// because the cancel action (deleteJoin) is itself per-event (#8). Rows with no
// event.id each become their own standalone group (legacy fallback). First-seen
// event order is preserved; the input array and its rows are never mutated.
export const groupBookingsByEvent = (rows: any[]): GroupedBooking[] => {
  const out: GroupedBooking[] = []
  const indexByEvent = new Map<string, number>()

  for (const row of rows) {
    const eventId: string = row?.event?.id
    const startTime: string = row?.event?.start_time
    const eventTitle: string | undefined = row?.title

    // No event id: can't aggregate — keep it standalone (legacy/solo row).
    const existing = eventId != null ? indexByEvent.get(eventId) : undefined
    if (existing == null) {
      if (eventId != null) indexByEvent.set(eventId, out.length)
      out.push({
        eventId,
        eventTitle,
        startTime,
        rows: [row],
        checkout: row?.checkout ?? null,
      })
    } else {
      out[existing].rows.push(row)
    }
  }

  return out
}

// Deprecated alias. Grouping is now per-event (#8): the events cancel endpoint
// cancels ALL of the member's bookings for an event, so the disclosure must
// aggregate per event, never per checkout. Kept so existing importers
// (UpcomingEvents, EventDetailCard) keep compiling and get the corrected,
// action-matching grouping for free.
export const groupBookingsByCheckout = groupBookingsByEvent

// Derive the all-or-nothing refund figure for a group from backend-authoritative
// inputs only. Refundable iff the backend-served start_time is more than
// (8h + honesty buffer) out from `now`. refundCents = Σ(row.total_cents) when
// refundable, else 0 (D-05). The comparison is a single millisecond subtraction —
// never a date-only equality, never toISOString().
export const deriveGroupRefund = (group: GroupedBooking, now: Date = new Date()): GroupCancelDisclosure => {
  const startMs = new Date(group.startTime).getTime()
  const refundable = startMs - now.getTime() > EIGHT_HOURS_MS + HONESTY_BUFFER_MS
  // Split by how each row was paid: paid_directly rows refund as cash (sum
  // total_cents); pack rows (paid_directly === false) refund as a returned credit
  // (one per row), not cash. A row missing paid_directly (legacy/pre-field) is
  // treated as money so behaviour is unchanged until the backend field is live.
  const moneyRows = group.rows.filter((r) => r?.paid_directly !== false)
  const creditRows = group.rows.filter((r) => r?.paid_directly === false)
  const refundCents = refundable ? moneyRows.reduce((s, r) => s + (r?.total_cents ?? 0), 0) : 0
  const creditCount = refundable ? creditRows.length : 0
  return { refundable, refundCents, creditCount }
}

// A natural-language Oxford-style list ("you", "Mia and Leo", "you, Mia, and Leo").
const joinNames = (names: string[]): string => {
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
}

// Build the affected-attendee sentence fragment for the group's rows. Project the
// rows into the participant shape buildBookedList expects (self row -> adult with
// id = member; child rows -> is_child with display_name = child_display_name and
// guardian_member_id = member), then reuse buildBookedList so DOB can never leak
// (WHO-01/D-06). The viewer's own row renders as "you".
export const buildAffectedNames = (rows: any[], viewerMemberId: string | null): string => {
  const participants: any[] = []
  for (const row of rows) {
    if (row?.child) {
      participants.push({
        id: row.child,
        display_name: row.child_display_name ?? 'Child',
        is_child: true,
        guardian_member_id: row.member,
      })
    } else {
      participants.push({
        id: row?.member,
        display_name: row?.display_name ?? 'You',
        is_child: false,
      })
    }
  }

  const booked = buildBookedList(participants, viewerMemberId)
  const names = booked.map((b) => (b.isYou ? 'you' : b.display_name))
  return joinNames(names)
}
